/**
 * LocationIQ reverse-geocode client. Caches results by 4-decimal coordinate
 * key so repeated lookups within ~10m don't refire.
 *
 * Returns `'המיקום שלך'` city when the API can't resolve a name, matching
 * the legacy `getDetailedAddress()` contract so callers don't need to special-case
 * missing data.
 */

import { LocationIq } from '../../app/config.js';
import { withTimeout } from '../../shared/promise.js';
import { createLogger } from '../../core/logger.js';

const log = createLogger('locationiq');

/**
 * @typedef {Object} ResolvedLocation
 * @property {string} city
 * @property {number} lat
 * @property {number} lon
 * @property {string} fullAddress
 * @property {string} [shortAddress] short "road + housenumber, city" form
 *                                   suitable for UI labels. Falls back to the
 *                                   road alone if no housenumber, or to
 *                                   neighbourhood/suburb if no road.
 */

/** @type {Map<string, ResolvedLocation>} */
const cache = new Map();

const FALLBACK_CITY = 'המיקום שלך';

/**
 * @param {number} lat
 * @param {number} lon
 * @returns {Promise<ResolvedLocation>}
 */
export async function reverseGeocode(lat, lon) {
  const key = `${lat.toFixed(4)},${lon.toFixed(4)}`;
  const cached = cache.get(key);
  if (cached) return cached;

  try {
    const url = new URL(LocationIq.baseUrl);
    url.searchParams.set('key', LocationIq.apiKey);
    url.searchParams.set('lat', String(lat));
    url.searchParams.set('lon', String(lon));
    url.searchParams.set('format', 'json');
    url.searchParams.set('accept-language', 'he');

    const res = await withTimeout(
      fetch(url.toString(), { headers: { Accept: 'application/json' } }),
      LocationIq.timeoutMs,
      'locationiq reverse',
    );
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    const a = json?.address ?? {};
    const city =
      a.city || a.town || a.village || a.municipality ||
      a.county || a.suburb || a.neighbourhood || FALLBACK_CITY;

    // Short form: "<road> <housenumber>, <city>" (no district / country tail).
    // Fall back gracefully if the address is sparser than usual.
    const road = a.road || a.pedestrian || a.footway || '';
    const num  = a.house_number || '';
    const area = a.neighbourhood || a.suburb || '';
    let shortAddress = '';
    if (road) {
      shortAddress = num ? `${road} ${num}` : road;
      if (city && city !== FALLBACK_CITY) shortAddress += `, ${city}`;
    } else if (area) {
      shortAddress = city && city !== FALLBACK_CITY ? `${area}, ${city}` : area;
    }

    const resolved = {
      city,
      lat,
      lon,
      fullAddress: String(json?.display_name ?? ''),
      shortAddress: shortAddress || undefined,
    };
    cache.set(key, resolved);
    return resolved;
  } catch (err) {
    log.warn('reverse-geocode failed:', /** @type {Error} */ (err).message);
    const fallback = { city: FALLBACK_CITY, lat, lon, fullAddress: '' };
    // Do NOT cache fallbacks — let the next call retry if the network recovered.
    return fallback;
  }
}

/** Clear the in-memory cache (e.g. on language change or logout). */
export function clearCache() {
  cache.clear();
}
